import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { EditorValue } from './EditorValue';
import type { ToolbarConfig } from './types';

interface Props {
  value: EditorValue;
  onChange: (value: EditorValue) => void;
  toolbarConfig?: ToolbarConfig;
  className?: string;
  toolbarClassName?: string;
  editorClassName?: string;
  placeholder?: string;
  toolbarStyle?: React.CSSProperties;
  editorStyle?: React.CSSProperties;
  readOnly?: boolean;
  disabled?: boolean;
}

const BLOCK_STYLE_TO_HEADER: Record<string, string | number> = {
  unstyled: '',
  'header-one': 1,
  'header-two': 2,
  'header-three': 3,
  'header-four': 4,
  'header-five': 5,
  'header-six': 6,
};

const INLINE_STYLE_TO_QUILL: Record<string, string> = {
  BOLD: 'bold',
  ITALIC: 'italic',
  UNDERLINE: 'underline',
  STRIKETHROUGH: 'strike',
  CODE: 'code',
};

const BLOCK_TYPE_TO_LIST: Record<string, string> = {
  'unordered-list-item': 'bullet',
  'ordered-list-item': 'ordered',
};

const QUILL_FORMATS = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'code',
  'list',
  'indent',
  'blockquote',
  'code-block',
] as const;

const DEFAULT_TOOLBAR_DISPLAY = [
  'BLOCK_TYPE_DROPDOWN',
  'INLINE_STYLE_BUTTONS',
  'BLOCK_TYPE_BUTTONS',
  'HISTORY_BUTTONS',
] as const;

const RTE_DROPDOWN_PORTAL_SEL = '[data-rte-toolbar-dropdown-portal]';

type HistoryCapableEditor = {
  history?: { undo: () => void; redo: () => void };
};

let toolbarCounter = 0;

function isQuillDocumentEmpty(quill: { getLength: () => number }): boolean {
  return quill.getLength() <= 1;
}

interface QuillCommitAdapter {
  getLength(): number;
  getFormat(
    index: number,
    length?: number,
  ): {
    header?: unknown;
    list?: unknown;
    blockquote?: unknown;
  };
  format(name: string, value: unknown): void;
  root: { innerHTML: string };
}

function computeCommittedHtml(
  quill: QuillCommitAdapter,
  incomingHtml: string,
  source: string,
  pendingHeaderRef: React.MutableRefObject<number | false | null>,
): string {
  let outHtml = incomingHtml;

  if (
    pendingHeaderRef.current !== null &&
    source === 'user' &&
    !isQuillDocumentEmpty(quill)
  ) {
    const pending = pendingHeaderRef.current;
    pendingHeaderRef.current = null;
    quill.format('header', pending === false ? false : pending);
    outHtml = quill.root.innerHTML;
  }

  if (
    source === 'user' &&
    isQuillDocumentEmpty(quill) &&
    pendingHeaderRef.current === null
  ) {
    const fmt = quill.getFormat(0);
    let normalized = false;
    if (fmt.header) {
      quill.format('header', false);
      normalized = true;
    }
    if (fmt.list) {
      quill.format('list', false);
      normalized = true;
    }
    if (fmt.blockquote) {
      quill.format('blockquote', false);
      normalized = true;
    }
    if (normalized) {
      outHtml = quill.root.innerHTML;
    }
  }

  return outHtml;
}

const RichTextEditor: React.FC<Props> = ({
  value,
  onChange,
  toolbarConfig,
  className,
  toolbarClassName,
  editorClassName,
  placeholder,
  toolbarStyle,
  editorStyle,
  readOnly,
  disabled,
}) => {
  const isReadOnly = readOnly || disabled;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<ReactQuill>(null);

  const [headerValue, setHeaderValue] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const dropdownRef = useRef<HTMLSpanElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  const lastSelectionRef = useRef<{ index: number; length: number } | null>(
    null,
  );
  const pendingHeaderRef = useRef<number | false | null>(null);

  const closeDropdownSoon = useCallback(() => {
    queueMicrotask(() => setDropdownOpen(false));
  }, []);

  useLayoutEffect(() => {
    if (!dropdownOpen) {
      setDropdownPos(null);
      return;
    }
    const wrap = wrapperRef.current;
    const btn = btnRef.current;
    if (!wrap || !btn) {
      setDropdownPos(null);
      return;
    }
    const wr = wrap.getBoundingClientRect();
    const br = btn.getBoundingClientRect();
    setDropdownPos({
      top: br.bottom - wr.top + wrap.scrollTop + 6,
      left: br.left - wr.left + wrap.scrollLeft,
    });
  }, [dropdownOpen]);

  const handleOpenDropdown = useCallback(() => {
    setDropdownOpen((o) => !o);
  }, []);

  useEffect(() => {
    if (!dropdownOpen) return;
    const handlePointerDownCapture = (e: MouseEvent) => {
      const target = e.target as Node;
      const el = target instanceof Element ? target : target.parentElement;
      if (dropdownRef.current?.contains(target)) return;
      if (dropdownMenuRef.current?.contains(target)) return;
      if (el?.closest(RTE_DROPDOWN_PORTAL_SEL)) return;
      closeDropdownSoon();
    };
    document.addEventListener('mousedown', handlePointerDownCapture, true);
    return () =>
      document.removeEventListener('mousedown', handlePointerDownCapture, true);
  }, [dropdownOpen, closeDropdownSoon]);

  const isMounted = useRef(false);
  useEffect(() => {
    isMounted.current = true;
  }, []);

  const toolbarId = useMemo(() => `rte-toolbar-${++toolbarCounter}`, []);

  const prevEditorStyle = useRef<React.CSSProperties | undefined>(undefined);
  const prevEditorClassName = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!wrapperRef.current) return;
    const editorEl =
      wrapperRef.current.querySelector<HTMLElement>('.ql-editor');
    if (!editorEl) return;

    if (prevEditorStyle.current) {
      (
        Object.keys(prevEditorStyle.current) as (keyof React.CSSProperties)[]
      ).forEach((key) => {
        editorEl.style[key as never] = '';
      });
    }
    if (editorStyle) {
      Object.assign(editorEl.style, editorStyle);
    }
    prevEditorStyle.current = editorStyle;

    prevEditorClassName.current
      ?.split(' ')
      .filter(Boolean)
      .forEach((cls) => editorEl.classList.remove(cls));
    editorClassName
      ?.split(' ')
      .filter(Boolean)
      .forEach((cls) => editorEl.classList.add(cls));
    prevEditorClassName.current = editorClassName;
  }, [editorStyle, editorClassName]);

  const toggleMark = useCallback((mark: 'bold' | 'italic' | 'underline') => {
    const q = quillRef.current?.getEditor();
    if (!q) return;
    const range = q.getSelection(true);
    if (!range) return;
    const fmt = q.getFormat(range);
    q.format(mark, !fmt[mark]);
  }, []);

  const runHistory = useCallback((which: 'undo' | 'redo') => {
    const editor = quillRef.current?.getEditor() as
      | HistoryCapableEditor
      | undefined;
    editor?.history?.[which]?.();
  }, []);

  const quillModules = useMemo(
    () => ({
      toolbar: isReadOnly
        ? false
        : {
            container: `#${toolbarId}`,
            handlers: {
              undo: () => runHistory('undo'),
              redo: () => runHistory('redo'),
              bold: () => toggleMark('bold'),
              italic: () => toggleMark('italic'),
              underline: () => toggleMark('underline'),
            },
          },
      history: { userOnly: true },
    }),
    [isReadOnly, toolbarId, toggleMark, runHistory],
  );

  const display = toolbarConfig?.display ?? DEFAULT_TOOLBAR_DISPLAY;

  const blockTypeOptions = toolbarConfig?.BLOCK_TYPE_DROPDOWN ?? [];
  const selectedLabel =
    blockTypeOptions.find((opt) => {
      const v = BLOCK_STYLE_TO_HEADER[opt.style];
      return (v === '' ? '' : String(v)) === headerValue;
    })?.label ?? blockTypeOptions[0]?.label;

  const rootClass = ['RichTextEditor__root', className]
    .filter(Boolean)
    .join(' ');

  const handleQuillChange = useCallback(
    (html: string, _delta: unknown, source: string) => {
      if (!isMounted.current) return;
      if (source === 'silent') return;
      const quill = quillRef.current?.getEditor() as
        | QuillCommitAdapter
        | undefined;
      const outHtml = quill
        ? computeCommittedHtml(quill, html, source, pendingHeaderRef)
        : html;
      onChange(EditorValue._fromHTML(outHtml));
    },
    [onChange],
  );

  const handleSelectionChange = useCallback(
    (range: { index: number; length: number } | null) => {
      if (!range || !quillRef.current) return;
      lastSelectionRef.current = range;
      if (pendingHeaderRef.current !== null) {
        const p = pendingHeaderRef.current;
        setHeaderValue(p === false ? '' : String(p));
        return;
      }
      const format = quillRef.current.getEditor().getFormat(range);
      setHeaderValue(format.header ? String(format.header) : '');
    },
    [],
  );

  return (
    <div ref={wrapperRef} className={rootClass}>
      {!isReadOnly && (
        <div
          id={toolbarId}
          className={['EditorToolbar', toolbarClassName]
            .filter(Boolean)
            .join(' ')}
          style={toolbarStyle}
        >
          {display.map((section) => {
            switch (section) {
              case 'BLOCK_TYPE_DROPDOWN': {
                if (!blockTypeOptions.length) return null;
                return (
                  <span
                    key={section}
                    className="ql-formats rte-dropdown-wrap"
                    ref={dropdownRef}
                  >
                    <button
                      ref={btnRef}
                      type="button"
                      className="rte-block-btn"
                      aria-expanded={dropdownOpen}
                      aria-haspopup="listbox"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleOpenDropdown();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleOpenDropdown();
                        }
                      }}
                    >
                      <span className="rte-block-label">{selectedLabel}</span>
                      <span className="rte-dropdown-arrow" aria-hidden="true">
                        ▾
                      </span>
                    </button>
                  </span>
                );
              }

              case 'INLINE_STYLE_BUTTONS': {
                const buttons = toolbarConfig?.INLINE_STYLE_BUTTONS ?? [];
                if (!buttons.length) return null;
                return (
                  <span key={section} className="ql-formats">
                    {buttons.map((btn) => {
                      const quillClass = INLINE_STYLE_TO_QUILL[btn.style];
                      if (!quillClass) return null;
                      return (
                        <button
                          key={btn.style}
                          type="button"
                          className={[`ql-${quillClass}`, btn.className]
                            .filter(Boolean)
                            .join(' ')}
                          data-label={btn.label}
                        />
                      );
                    })}
                  </span>
                );
              }

              case 'BLOCK_TYPE_BUTTONS': {
                const buttons = toolbarConfig?.BLOCK_TYPE_BUTTONS ?? [];
                if (!buttons.length) return null;
                return (
                  <span key={section} className="ql-formats">
                    {buttons.map((btn) => {
                      const listVal = BLOCK_TYPE_TO_LIST[btn.style];
                      if (!listVal) return null;
                      return (
                        <button
                          key={btn.style}
                          type="button"
                          className={['ql-list', btn.className]
                            .filter(Boolean)
                            .join(' ')}
                          value={listVal}
                        />
                      );
                    })}
                  </span>
                );
              }

              case 'HISTORY_BUTTONS':
                return (
                  <span key={section} className="ql-formats">
                    <button className="ql-undo" title="Undo">
                      <svg viewBox="0 0 18 18">
                        <polygon
                          className="ql-fill ql-stroke"
                          points="6 10 4 12 2 10 6 10"
                        />
                        <path
                          className="ql-stroke"
                          d="M8.09,13.91A4.6,4.6,0,0,0,9,14,5,5,0,1,0,4,9"
                        />
                      </svg>
                    </button>
                    <button className="ql-redo" title="Redo">
                      <svg viewBox="0 0 18 18">
                        <polygon
                          className="ql-fill ql-stroke"
                          points="12 10 14 12 16 10 12 10"
                        />
                        <path
                          className="ql-stroke"
                          d="M9.91,13.91A4.6,4.6,0,0,1,9,14a5,5,0,1,1,5-5"
                        />
                      </svg>
                    </button>
                  </span>
                );

              default:
                return null;
            }
          })}
        </div>
      )}

      {dropdownOpen &&
        dropdownPos &&
        blockTypeOptions.length > 0 &&
        wrapperRef.current &&
        createPortal(
          <div
            ref={dropdownMenuRef}
            className="rte-block-options"
            data-rte-toolbar-dropdown-portal
            style={{
              position: 'absolute',
              top: dropdownPos.top,
              left: dropdownPos.left,
            }}
          >
            {blockTypeOptions.map((opt) => {
              const v = BLOCK_STYLE_TO_HEADER[opt.style];
              const strVal = v === '' ? '' : String(v);
              const headerArg = v === '' ? false : v;
              return (
                <button
                  key={opt.style}
                  type="button"
                  className={[
                    'rte-block-option',
                    headerValue === strVal ? 'rte-block-option--active' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    const quill = quillRef.current?.getEditor();
                    if (!quill) return;
                    if (isQuillDocumentEmpty(quill)) {
                      pendingHeaderRef.current =
                        headerArg === false ? false : (headerArg as number);
                      setHeaderValue(strVal);
                      quill.focus();
                      closeDropdownSoon();
                      return;
                    }
                    pendingHeaderRef.current = null;
                    const saved = lastSelectionRef.current;
                    if (saved) {
                      quill.setSelection(saved.index, saved.length, 'user');
                    } else {
                      const end = Math.max(0, quill.getLength() - 1);
                      quill.setSelection(end, 0, 'user');
                    }
                    quill.format('header', headerArg);
                    setHeaderValue(strVal);
                    quill.focus();
                    closeDropdownSoon();
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>,
          wrapperRef.current,
        )}

      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value.getHTML()}
        onChange={handleQuillChange}
        onChangeSelection={handleSelectionChange}
        placeholder={placeholder}
        readOnly={isReadOnly}
        modules={quillModules}
        formats={[...QUILL_FORMATS]}
        className="RichTextEditor__editor"
      />
    </div>
  );
};

export default RichTextEditor;
